import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Button } from "design-system/atoms/Button"
import { Textarea } from "design-system/atoms/Textarea"
import { Card } from "design-system/molecules/Card"
import { AlertCircle, Check, Copy, Replace, X } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

export type ComposerPromptValidationIssue = {
  field: string
  requirementId: string
  explanation: string
  excerpt: string
  startLine: number
  endLine: number
  suggestedAlternative: string
}

type Props = {
  issues: ComposerPromptValidationIssue[]
  getFieldText?: (field: string) => string
  onApplySuggestion?: (field: string, text: string) => void
  onOpenChange?: (open: boolean) => void
}

export function ComposerPromptValidationIssues({
  issues,
  getFieldText,
  onApplySuggestion,
  onOpenChange,
}: Props) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [copiedIssue, setCopiedIssue] = useState<string | null>(null)
  const [editingIssue, setEditingIssue] = useState<string | null>(null)
  const [manualSuggestion, setManualSuggestion] = useState("")
  const [appliedIssues, setAppliedIssues] = useState<Set<string>>(new Set())
  if (issues.length === 0) return null

  async function copySuggestion(issue: ComposerPromptValidationIssue) {
    await navigator.clipboard.writeText(issue.suggestedAlternative)
    setCopiedIssue(`${issue.field}:${issue.requirementId}`)
    toast.success(t("shared.composerValidation.copied"))
  }

  function applySuggestion(issue: ComposerPromptValidationIssue, replacement: string) {
    const text = getFieldText?.(issue.field)
    if (!text || text.indexOf(issue.excerpt) !== text.lastIndexOf(issue.excerpt)) return
    onApplySuggestion?.(issue.field, text.replace(issue.excerpt, replacement))
    setEditingIssue(null)
    const appliedIssueKey = `${issue.field}:${issue.requirementId}`
    setAppliedIssues((applied) => new Set(applied).add(appliedIssueKey))
    toast.success(t("shared.composerValidation.applied"))
    const hasRemainingIssues = issues.some((candidate) => {
      const candidateKey = `${candidate.field}:${candidate.requirementId}`
      return candidateKey !== appliedIssueKey && !appliedIssues.has(candidateKey)
    })
    if (!hasRemainingIssues) handleOpenChange(false)
  }

  function startManualEdit(issue: ComposerPromptValidationIssue) {
    setEditingIssue(`${issue.field}:${issue.requirementId}`)
    setManualSuggestion(issue.suggestedAlternative)
  }

  function cancelManualEdit() {
    setEditingIssue(null)
    setManualSuggestion("")
  }

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen)
    if (nextOpen) setAppliedIssues(new Set())
    if (!nextOpen) cancelManualEdit()
    onOpenChange?.(nextOpen)
  }

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="text-destructive"
        onClick={() => handleOpenChange(true)}
        aria-label={t("shared.composerValidation.openIssues")}
      >
        <AlertCircle className="h-5 w-5" />
      </Button>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent size="3xl" className="flex max-h-[90dvh] flex-col overflow-hidden">
          <DialogHeader className="shrink-0 pr-8">
            <DialogTitle>{t("shared.composerValidation.title")}</DialogTitle>
            <DialogDescription>{t("shared.composerValidation.description")}</DialogDescription>
          </DialogHeader>
          <div className="flex min-h-0 flex-col gap-3 overflow-y-auto pr-1">
            {issues.map((issue) => {
              const key = `${issue.field}:${issue.requirementId}`
              if (appliedIssues.has(key)) return null
              const isCopied = copiedIssue === key
              const isEditing = editingIssue === key
              const text = getFieldText?.(issue.field)
              const canApply =
                Boolean(onApplySuggestion && text) &&
                text!.indexOf(issue.excerpt) === text!.lastIndexOf(issue.excerpt)

              return (
                <div key={key} className="rounded-lg border bg-card p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {t("shared.composerValidation.lines", {
                          start: issue.startLine,
                          end: issue.endLine,
                        })}
                      </p>
                    </div>
                  </div>
                  <p className="mt-3 text-sm">{issue.explanation}</p>
                  <div className="mt-3 rounded-md border border-destructive/20 bg-destructive/5 p-3">
                    <p className="text-xs font-medium text-destructive">
                      {t("shared.composerValidation.textToReview")}
                    </p>
                    <code className="mt-1 block whitespace-pre-wrap text-sm">{issue.excerpt}</code>
                  </div>
                  <Card className="mt-3 border-primary/20 bg-primary/5 p-3 shadow-none">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-medium">
                        {t("shared.composerValidation.tryThis")}
                      </p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="-mr-2 -mt-2 h-7 w-7 shrink-0"
                        onClick={() => void copySuggestion(issue)}
                        aria-label={t("shared.composerValidation.copySuggestion")}
                      >
                        {isCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                    <p className="mt-2 text-sm">{issue.suggestedAlternative}</p>
                    {isEditing && (
                      <Textarea
                        className="mt-3"
                        value={manualSuggestion}
                        onChange={(event) => setManualSuggestion(event.target.value)}
                        placeholder={t("shared.composerValidation.manualSuggestionPlaceholder")}
                      />
                    )}
                  </Card>
                  <div className="mt-3 flex justify-end gap-2">
                    {isEditing ? (
                      <>
                        <Button
                          type="button"
                          size="icon"
                          disabled={!canApply || !manualSuggestion.trim()}
                          onClick={() => applySuggestion(issue, manualSuggestion)}
                          aria-label={t("shared.composerValidation.applyManualSuggestion")}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={cancelManualEdit}
                          aria-label={t("shared.composerValidation.cancelManualSuggestion")}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => startManualEdit(issue)}
                        >
                          {t("shared.composerValidation.changeManually")}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          disabled={!canApply}
                          onClick={() => applySuggestion(issue, issue.suggestedAlternative)}
                        >
                          <Replace className="h-4 w-4" />
                          {t("shared.composerValidation.applySuggestion")}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
